import { AIGradingModeEnum, QuestionTypeEnum } from "@ai-teach/types";

import type {
  GrammarResponseSlideData,
  GrammarSlideData,
  HandWritingSlideData,
  PhonicsDecodeSlideData,
  PhonicsEncodeSlideData,
  ReadingSlideData,
  SlideElement,
  SlideLimitation,
  SlideTemplate,
  WordSlideData,
  WordVocabularySlideData,
} from "@ai-teach/types";

import { validateGrammarFormAuthoring } from "./validateGrammarFormAuthoring.js";

export interface SlideWarningItem {
  // "warning" = advisory content-quality flag (e.g. a grammar mistake in the authored dialogue); it does
  // NOT block slide completion (see isSlideCompleted). "incomplete" = a missing required asset (blocks).
  // "error" (round 8) = invalid authored content that would break the runtime (e.g. stray { } [ ] or __
  // in grammar_focus) — a distinct, obvious flag that ALSO blocks completion/publish.
  asset: string;
  status: "complete" | "incomplete" | "warning" | "error";
  current?: number;
  required?: number;
}

export interface SlideWarningBlock {
  warningItem: SlideWarningItem[];
  title: string;
}

/**
 * Input shape for getSlideWarning — only the fields needed for the check.
 * Works with both frontend GraphQL fragments and backend DB documents.
 */
export interface SlideWarningInput {
  template: SlideTemplate | null | undefined;
  slideData: unknown;
  limitation?: SlideLimitation | null | undefined;
  elements: SlideElement[];
}

export function getSlideWarning(slide: SlideWarningInput | null): SlideWarningBlock[] {
  if (!slide) return [];

  // Word Slide Warning
  if (slide.template === "vocabulary") {
    const wordSlideData = slide.slideData as WordSlideData;

    const wordSlideWarning: SlideWarningItem[] = [
      {
        asset: "Image",
        status: wordSlideData.image.src ? "complete" : "incomplete",
      },
      {
        asset: "Audio",
        status: wordSlideData.wordAudio.src ? "complete" : "incomplete",
      },
      {
        asset: "Word",
        status: wordSlideData.word ? "complete" : "incomplete",
      },
    ];

    return [
      {
        title: "Vocabulary Slide",
        warningItem: wordSlideWarning,
      },
    ];
  }

  // Word Vocabulary Slide Warning
  if (slide.template === "listenRepeat") {
    const vocabularySlideData = slide.slideData as WordVocabularySlideData;

    // The card carries either an image or a video, so only the selected one counts.
    const isVideoCard = vocabularySlideData.mediaType === "video";
    const cardMediaSrc = isVideoCard
      ? vocabularySlideData.video?.src
      : vocabularySlideData.image.src;

    // Video mode needs an actual video; image mode is satisfied by an image, audio, or both.
    const mediaComplete = isVideoCard
      ? !!cardMediaSrc
      : !!(cardMediaSrc || vocabularySlideData.audio.src);

    const vocabularySlideWarning: SlideWarningItem[] = [
      {
        asset: isVideoCard ? "Video" : "Image and/or Audio",
        status: mediaComplete ? "complete" : "incomplete",
      },
      ...(vocabularySlideData.gradingMode !== AIGradingModeEnum.Values.contextMode
        ? [
            {
              asset:
                vocabularySlideData.gradingMode === AIGradingModeEnum.Values.grammarMode
                  ? "Grammar Template"
                  : "Correct Answer",
              status: (vocabularySlideData.correctAnswer &&
              vocabularySlideData.correctAnswer !== "Your Answer" &&
              vocabularySlideData.correctAnswer !== ""
                ? "complete"
                : "incomplete") as "complete" | "incomplete",
            },
          ]
        : []),
      {
        asset: "Question",
        status:
          vocabularySlideData.instructionText && vocabularySlideData.instructionText !== ""
            ? "complete"
            : "incomplete",
      },
    ];

    return [
      {
        title: "Listen & Repeat Slide",
        warningItem: vocabularySlideWarning,
      },
    ];
  }

  // Grammar Form Slide Warning
  if (slide.template === "conversation") {
    // Teachers author FOCUS (the words/grammar this slide teaches). The strict, shared authoring
    // validator is the single source of truth for whether the authored content is publishable.
    const grammarFocus = slide.limitation?.grammarFocus;
    const wordFocus = slide.limitation?.wordFocus;

    // Presence rows keep the editor's completion checklist familiar. The validator's *_required codes
    // are skipped below so a missing field is reported ONCE (as "incomplete" here), never duplicated as
    // a second "error" item for the same field.
    const grammarSlideWarning: SlideWarningItem[] = [
      {
        asset: "Grammar focus",
        // Trim so whitespace-only ("   ") reads as incomplete here too — otherwise the checklist shows
        // "complete" while the server's `grammar_focus_required` (which trims) rejects publication.
        status: grammarFocus?.trim() ? "complete" : "incomplete",
      },
      {
        asset: "Word focus",
        status: wordFocus?.trim() ? "complete" : "incomplete",
      },
    ];

    const { issues } = validateGrammarFormAuthoring({ grammarFocus, wordFocus });
    for (const issue of issues) {
      // Missing-field errors are already surfaced by the presence rows above.
      if (issue.code === "grammar_focus_required" || issue.code === "word_focus_required") {
        continue;
      }
      grammarSlideWarning.push({
        asset: issue.line !== null ? `Line ${issue.line}: ${issue.message}` : issue.message,
        status: issue.severity === "warning" ? "warning" : "error",
      });
    }

    return [
      {
        title: "AI Conversation Slide",
        warningItem: grammarSlideWarning,
      },
    ];
  }

  if (slide.template === "audioRevealPrompt") {
    const grammarSlideData = slide.slideData as GrammarSlideData;

    const sentenceSlideWarning: SlideWarningItem[] = [
      {
        asset: "Image",
        status: grammarSlideData.image?.src ? "complete" : "incomplete",
      },
      {
        asset: "Audio",
        status: grammarSlideData.audio?.src ? "complete" : "incomplete",
      },
      {
        asset: "Question",
        status: grammarSlideData.text ? "complete" : "incomplete",
      },
      {
        asset: "Expected Answer",
        status: grammarSlideData.correctAnswer ? "complete" : "incomplete",
      },
    ];

    return [
      {
        title: "Audio Reveal Prompt Slide",
        warningItem: sentenceSlideWarning,
      },
    ];
  }

  if (slide.template === "videoQuestion") {
    const grammarSlideData = slide.slideData as GrammarResponseSlideData;

    const slideWarnings: SlideWarningBlock[] = [];

    const sentenceSlideWarning: SlideWarningItem[] = [
      grammarSlideData.mediaType === "audio"
        ? {
            asset: "Audio",
            status: grammarSlideData.audio?.src ? "complete" : "incomplete",
          }
        : {
            asset: "Video",
            status: grammarSlideData.video?.src ? "complete" : "incomplete",
          },
    ];

    if (grammarSlideData.checkpoints.length === 0) {
      sentenceSlideWarning.push({ asset: "Checkpoint", status: "incomplete" });
    }

    if (grammarSlideData.firstQuestionTime === 0) {
      sentenceSlideWarning.push({ asset: "First Question Timestamp", status: "incomplete" });
    }

    slideWarnings.push({
      title: "Video Questions Slide",
      warningItem: sentenceSlideWarning,
    });

    for (const [index, checkpoint] of grammarSlideData.checkpoints.entries()) {
      const checkpointWarning: SlideWarningItem[] = [];

      checkpointWarning.push({
        asset: "Question",
        status: checkpoint.question ? "complete" : "incomplete",
      });

      if (checkpoint.gradingMode !== AIGradingModeEnum.Values.contextMode) {
        checkpointWarning.push({
          asset: "Answer",
          status: checkpoint.answer ? "complete" : "incomplete",
        });
      }
      slideWarnings.push({ title: `Checkpoint ${index + 1}`, warningItem: checkpointWarning });
    }

    return slideWarnings;
  }

  // Phonics Encode Slide Warning
  if (slide.template === "soundToSpelling") {
    const sd = slide.slideData as PhonicsEncodeSlideData;
    const warningItems: SlideWarningItem[] = [
      {
        asset: "Phonics Audio",
        status: sd.audio?.src ? "complete" : "incomplete",
      },
      {
        asset: "Correct Answer",
        status: sd.correctAnswer ? "complete" : "incomplete",
      },
    ];

    if (sd.questionMode === "multiple_choice") {
      warningItems.push({
        asset: "Distractor Options",
        status: sd.options.length > 0 ? "complete" : "incomplete",
        current: sd.options.length,
        required: 1,
      });
    }

    return [{ title: "Sound to Spelling Slide", warningItem: warningItems }];
  }

  // Phonics Decode Slide Warning
  if (slide.template === "spellingToSound") {
    const sd = slide.slideData as PhonicsDecodeSlideData;
    return [
      {
        title: "Spelling to Sound Slide",
        warningItem: [
          {
            asset: "Phoneme",
            status: sd.phoneme ? "complete" : "incomplete",
          },
          {
            asset: "Reference Audio",
            status: sd.audio?.src ? "complete" : "incomplete",
          },
        ],
      },
    ];
  }

  // Hand Writing Slide Warning — the image is optional, only the words are required.
  if (slide.template === "handWriting") {
    const sd = slide.slideData as HandWritingSlideData;
    const filledText = (sd.text ?? []).filter((t) => t.trim() !== "").length;

    return [
      {
        title: "Hand Writing Slide",
        warningItem: [
          {
            asset: "Text",
            status: filledText > 0 ? "complete" : "incomplete",
            current: filledText,
            required: 1,
          },
        ],
      },
    ];
  }

  // Reading Slide Warning
  if (slide.template === "passageReading") {
    const slideWarnings: SlideWarningBlock[] = [];

    const readingSlideData = slide.slideData as ReadingSlideData;

    if (readingSlideData.passages.length === 0) {
      return [
        {
          title: "Passage Reading Slide",
          warningItem: [{ asset: "Passage", status: "incomplete" }],
        },
      ];
    }

    for (const [index, passage] of readingSlideData.passages.entries()) {
      const passageWarning: SlideWarningItem[] = [];

      // A passage is authored one of two ways: image (optional) with audio, or video alone — a
      // clip's own soundtrack is the model read, so a video passage needs no audio.
      const isVideoPassage = !readingSlideData.isTextOnly && passage.mediaType === "video";

      passageWarning.push(
        isVideoPassage
          ? {
              asset: "Video",
              status: passage.video?.src ? "complete" : "incomplete",
            }
          : {
              asset: "Audio",
              status: passage.audio ? "complete" : "incomplete",
            },
        {
          asset: "Text",
          status: passage.text ? "complete" : "incomplete",
        },
        {
          asset: "Spell check",
          status: passage.spellCheckStatus === "passed" ? "complete" : "incomplete",
        }
      );

      slideWarnings.push({ title: `Passage ${index + 1}`, warningItem: passageWarning });
    }

    return slideWarnings;
  }

  const checkElementWarning = (element: SlideElement): SlideWarningBlock | null => {
    const elementType = element.type;

    if (elementType === "textChoice") {
      const displayText = `${element.text.slice(0, 26)}${element.text.length > 26 ? "..." : ""}`;

      return {
        title: `Text Choice - ${displayText}`,
        warningItem: [
          {
            asset: "Text",
            status: element.text ? "complete" : "incomplete",
          },
        ],
      };
    }

    if (elementType === "imageChoice") {
      return {
        title: "Image Choice",
        warningItem: [
          {
            asset: "Image",
            status: element.media.src ? "complete" : "incomplete",
          },
        ],
      };
    }

    if (elementType === "text") {
      const displayText = `${element.content.slice(0, 26)}${element.content.length > 26 ? "..." : ""}`;
      return {
        title: `Text Element - ${displayText}`,
        warningItem: [
          {
            asset: "Text",
            status: element.content && element.content.trim() !== "" ? "complete" : "incomplete",
          },
        ],
      };
    }

    if (elementType === "dragMatchingText") {
      const elementWarnings: SlideWarningItem[] = [];

      elementWarnings.push({
        asset: "Image",
        status: element.media.src ? "complete" : "incomplete",
      });

      let textMissing = 0;
      const textTotal = element.textChoices.length;

      for (const choice of element.textChoices) {
        if (!choice.text || choice.text.trim() === "") {
          textMissing++;
        }
      }

      elementWarnings.push({
        asset: "Text",
        status: textMissing === 0 ? "complete" : "incomplete",
        current: textTotal - textMissing,
        required: textTotal,
      });

      return {
        title: "Drag Matching Text",
        warningItem: elementWarnings,
      };
    }

    if (elementType === "dragMatchingImage") {
      const elementWarnings: SlideWarningItem[] = [];

      elementWarnings.push({
        asset: "Text",
        status: element.text ? "complete" : "incomplete",
      });

      let imageMissing = 0;
      const imageTotal = element.imageChoices.length;

      for (const choice of element.imageChoices) {
        if (!choice.media.src) {
          imageMissing++;
        }
      }

      elementWarnings.push({
        asset: "Image",
        status: imageMissing === 0 ? "complete" : "incomplete",
        current: imageTotal - imageMissing,
        required: imageTotal,
      });

      return {
        title: "Drag Matching Image",
        warningItem: elementWarnings,
      };
    }

    if (elementType === "oddTextOut") {
      let textMissing = 0;
      const textTotal = element.textChoices.length;

      for (const choice of element.textChoices) {
        if (!choice.text || choice.text.trim() === "") {
          textMissing++;
        }
      }

      return {
        title: "Odd Text Out",
        warningItem: [
          {
            asset: "Text",
            status: textMissing === 0 ? "complete" : "incomplete",
            current: textTotal - textMissing,
            required: textTotal,
          },
        ],
      };
    }

    if (elementType === "oddImageOut") {
      let imageMissing = 0;
      const imageTotal = element.imageChoices.length;

      for (const choice of element.imageChoices) {
        if (!choice.media.src) {
          imageMissing++;
        }
      }

      return {
        title: "Odd Text Out",
        warningItem: [
          {
            asset: "Image",
            status: imageMissing === 0 ? "complete" : "incomplete",
            current: imageTotal - imageMissing,
            required: imageTotal,
          },
        ],
      };
    }

    if (elementType === "fillBlanks") {
      const elementWarnings: SlideWarningItem[] = [];
      const isTextOnly = element.isTextOnly;

      // Check question text
      elementWarnings.push({
        asset: "Question",
        status: element.text && element.text.trim() !== "" ? "complete" : "incomplete",
      });

      // Check each block
      for (const [i, block] of element.blocks.entries()) {
        // Check image if not text-only
        if (!isTextOnly) {
          elementWarnings.push({
            asset: `Blank ${i + 1} Image`,
            status: block.media?.src ? "complete" : "incomplete",
          });
        }

        // Check options text
        let textMissing = 0;
        const textTotal = block.options.length;

        for (const choice of block.options) {
          if (!choice.text || choice.text.trim() === "") {
            textMissing++;
          }
        }

        elementWarnings.push({
          asset: `Blank ${i + 1} Options`,
          status: textMissing === 0 ? "complete" : "incomplete",
          current: textTotal - textMissing,
          required: textTotal,
        });
      }

      return { title: "Fill in the blank", warningItem: elementWarnings };
    }

    if (elementType === "spelling") {
      return {
        title: "Spelling",
        warningItem: [
          {
            asset: "Word",
            status: element.word && element.word.trim() !== "" ? "complete" : "incomplete",
          },
        ],
      };
    }

    if (elementType === "composition") {
      return {
        title: "Composition",
        warningItem: [
          {
            asset: "Prompt",
            status: element.prompt && element.prompt.trim() !== "" ? "complete" : "incomplete",
          },
        ],
      };
    }

    if (elementType === "speaking") {
      const elementWarnings: SlideWarningItem[] = [];

      for (const [i, sentence] of element.sentences.entries()) {
        elementWarnings.push({
          asset: `Sentence ${i + 1} Text`,
          status: sentence.text && sentence.text.trim() !== "" ? "complete" : "incomplete",
        });

        elementWarnings.push({
          asset: `Sentence ${i + 1} Audio`,
          status: sentence.audio?.src ? "complete" : "incomplete",
        });

        elementWarnings.push({
          asset: `Sentence ${i + 1} Options`,
          status: sentence.options.length >= 4 ? "complete" : "incomplete",
          current: sentence.options.length,
          required: 4,
        });

        elementWarnings.push({
          asset: `Sentence ${i + 1} Correct Answer`,
          status: sentence.options.some((o) => o.isCorrectAnswer) ? "complete" : "incomplete",
        });
      }

      return { title: "Speaking", warningItem: elementWarnings };
    }

    if (elementType === "answerImage") {
      return {
        title: "Question and Answer",
        warningItem: [
          {
            asset: "Image",
            status: element.media.src ? "complete" : "incomplete",
          },
          {
            asset: "Question Text",
            status: element.questionText.trim() ? "complete" : "incomplete",
          },
          {
            asset: "Answer Text",
            status: element.correctAnswer.trim() ? "complete" : "incomplete",
          },
        ],
      };
    }

    if (elementType === "orderImage") {
      const total = element.items.length;
      const imageMissing = element.items.filter((it) => !it.media.src).length;
      const orderMissing = element.items.filter((it) => it.order === undefined).length;

      return {
        title: "Order Image",
        warningItem: [
          {
            asset: "Images",
            status: imageMissing === 0 ? "complete" : "incomplete",
            current: total - imageMissing,
            required: total,
          },
          {
            asset: "Answer Order",
            status: orderMissing === 0 ? "complete" : "incomplete",
            current: total - orderMissing,
            required: total,
          },
        ],
      };
    }

    return null;
  };

  const slideWarnings: SlideWarningBlock[] = [];

  if (slide.elements && slide.elements.length === 0) {
    return [
      {
        title: "Element",
        warningItem: [
          {
            asset: "No Element",
            status: "incomplete",
          },
        ],
      },
    ];
  }

  if (slide.elements?.some((el) => el.type === "imageChoice" || el.type === "textChoice")) {
    const choiceElements = slide.elements.filter(
      (el) => el.type === "imageChoice" || el.type === "textChoice"
    );

    if (!choiceElements.some((el) => el.isCorrectAnswer)) {
      slideWarnings.push({
        title: "Slide Correct Answer",
        warningItem: [
          {
            asset: "Needs 1 correct answer",
            status: "incomplete",
          },
        ],
      });
    }
  }

  if (slide.elements) {
    // `composition` satisfies "this slide gives the student a task" even though
    // it is deliberately outside QuestionTypeEnum (that enum gates the
    // scoring/completion machinery, which composition must not enter).
    if (
      !slide.elements.some((el) => el.type in QuestionTypeEnum.Values || el.type === "composition")
    ) {
      slideWarnings.push({
        title: "Slide Question",
        warningItem: [
          {
            status: "incomplete",
            asset: "Needs 1 question type",
          },
        ],
      });
    }

    for (const element of slide.elements) {
      const elementWarning = checkElementWarning(element);
      if (elementWarning) {
        slideWarnings.push(elementWarning);
      }
    }
  }

  return slideWarnings;
}
